import { ref, computed, shallowRef } from "vue";
import { defineStore } from "pinia";
import type { WordleLeaderboardDocument } from "@kekkle/shared/types/documents/wordle-leaderboard-document/wordle-leaderboard-document";
import type { QueryDocumentSnapshot, DocumentData } from "firebase/firestore";
import { fetchLeaderBoard } from "@/helpers/firestore/WordleLeaderBoardService";
import {
  getBooleanFromLocalStorage,
  setLocalStorageItem,
} from "@/helpers/utils/local-storage";
import { useGroupsStore } from "@/stores/modules/groups/groups";

/**
 * Wordle leaderboard store for managing total leaderboard data
 * Uses Pinia with Composition API and TypeScript
 */
export const useWordleLeaderboardStore = defineStore(
  "wordleLeaderboard",
  () => {
    const groupsStore = useGroupsStore();

    /* State */
    const leaderboard = ref<WordleLeaderboardDocument[]>([]);
    const fetchingLeaderboard = ref<boolean>(false);
    const onlyKnownUsers = ref<boolean>(
      getBooleanFromLocalStorage("onlyKnownUsersWorldeLeaderboard", true),
    );
    // Use shallowRef to prevent deep reactivity on Firestore snapshots
    const lastDocument = shallowRef<
      QueryDocumentSnapshot<DocumentData> | undefined
    >();
    const hasMoreData = ref<boolean>(false);

    /* Computed / Getters */
    const currentLeaderboard = computed(() => leaderboard.value);
    const isFetchingLeaderboard = computed(() => fetchingLeaderboard.value);
    const hasMore = computed(() => hasMoreData.value);

    /* Actions */
    function setOnlyKnownUsersWorldeLeaderboard(value: boolean): void {
      onlyKnownUsers.value = value;
      setLocalStorageItem(
        "onlyKnownUsersWorldeLeaderboard",
        JSON.stringify(value),
      );
    }

    function reset(): void {
      leaderboard.value = [];
      fetchingLeaderboard.value = false;
      onlyKnownUsers.value = true;
      lastDocument.value = undefined;
      hasMoreData.value = false;
      setLocalStorageItem("onlyKnownUsersWorldeLeaderboard", "true");
    }

    async function fetchLeaderboardAction(loadMore = false): Promise<void> {
      fetchingLeaderboard.value = true;

      let userFilter: string[] = [];
      if (onlyKnownUsers.value) {
        userFilter = groupsStore.allGroupMembersUuids;
      }

      try {
        const lastDoc = loadMore ? lastDocument.value : undefined;
        const result = await fetchLeaderBoard(userFilter, {
          pageSize: 5,
          lastDoc,
        });

        if (loadMore) {
          leaderboard.value = [...leaderboard.value, ...result.docs];
        } else {
          leaderboard.value = result.docs;
        }

        // Store pagination state
        lastDocument.value = result.lastDoc;
        hasMoreData.value = result.hasMore;
      } catch (error) {
        console.error("Error fetching leaderboard:", error);
        if (!loadMore) {
          leaderboard.value = [];
        }
      } finally {
        fetchingLeaderboard.value = false;
      }
    }

    /* Exposed store */
    return {
      // State
      leaderboard,
      fetchingLeaderboard,
      onlyKnownUsers,
      lastDocument,
      hasMoreData,

      // Computed
      currentLeaderboard,
      isFetchingLeaderboard,
      hasMore,

      // Actions
      setOnlyKnownUsersWorldeLeaderboard,
      reset,
      fetchLeaderboard: fetchLeaderboardAction,
    };
  },
);
